import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Globe, LogOut, X, Menu, Sun, Moon, Volume2, VolumeX, KeyRound } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth, API } from "../api";
import { useTheme } from "../theme";
import { sfx } from "../sfx";
import { ASSETS } from "../assets";
import { PasswordModal } from "./PasswordModal";

export const Shell = ({ children, nav = [], variant = "pro", right = null }) => {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [sound, setSound] = useState(sfx.enabled());
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const play = variant === "play";
  const brand = user?.school_color;
  const accent = user?.school_accent || (play ? "var(--play-primary)" : "var(--pro-secondary)");
  const logoUrl = user?.school_logo_id
    ? `${API}/schools/${user.school_id}/logo?t=${user.school_logo_id}&auth=${localStorage.getItem("el_token")}`
    : null;

  useEffect(() => {
    const root = document.documentElement;
    if (brand) root.style.setProperty(play ? "--play-primary" : "--pro-secondary", brand);
    if (user?.school_accent) root.style.setProperty(play ? "--play-secondary" : "--sky", user.school_accent);
  }, [brand, user?.school_accent, play]);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const sidebar = (
    <aside className={`fixed lg:sticky top-0 z-50 h-screen w-64 shrink-0 flex flex-col gap-2 p-4
      transition-transform duration-300 ${lang === "ar" ? "end-0" : "start-0"}
      ${open ? "translate-x-0" : `invisible pointer-events-none ${lang === "ar" ? "translate-x-full" : "-translate-x-full"}`}
      lg:visible lg:pointer-events-auto lg:translate-x-0`}
      style={{ background: "linear-gradient(180deg,#0a1738 0%,#08132c 100%)",
        borderInlineEnd: "1px solid rgba(255,255,255,.09)" }}>
      <div className="flex items-center justify-between">
        <Link to="/" className="block px-1 py-2">
          <img src={ASSETS.logo} alt="English Lab" className="w-44 mix-blend-screen soft-mask-wide" />
        </Link>
        <button onClick={() => setOpen(false)} className="lg:hidden text-white/70 p-2" data-testid="sidebar-close">
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto no-scrollbar mt-2 space-y-1.5">
        {nav.map((n) => {
          const active = loc.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.key}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all
                ${active ? "text-white shadow-lg" : "text-white/65 hover:text-white hover:bg-white/10"}`}
              style={active ? { background: accent, boxShadow: "0 6px 18px rgba(0,0,0,.35)" } : {}}>
              {n.icon && <img src={n.icon} alt="" className="w-7 h-7 object-contain mix-blend-screen soft-mask" />}
              {n.label}
            </Link>
          );
        })}
        {nav.length < 3 && (
          <div className="mt-6 px-2">
            <img src={ASSETS.mascot} alt="" className="w-full mix-blend-screen soft-mask float-slow" />
            <p className="text-center text-white/50 text-xs font-bold mt-2">English Lab · {t(user?.role || "")}</p>
          </div>
        )}
      </nav>

      <div className="glass-soft p-3 flex items-center gap-3">
        {logoUrl ? (
          <img src={logoUrl} alt="logo" data-testid="school-logo"
            className="w-10 h-10 rounded-xl object-cover border border-white/20" />
        ) : (
          <img src={ASSETS.mascot} alt="" className="w-10 h-10 rounded-xl object-cover object-top mix-blend-screen soft-mask" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-extrabold text-white truncate">{user?.full_name}</div>
          <div className="text-xs text-white/55">{t(user?.role || "student")}</div>
        </div>
        <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} title="تغيير كلمة السر"
          className="p-2 rounded-xl bg-white/10 text-white/80 hover:bg-white/20"><KeyRound size={15} /></button>
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
          className="p-2 rounded-xl bg-white/10 text-white/80 hover:bg-white/20"><LogOut size={15} /></button>
      </div>
    </aside>
  );

  return (
    <div className="app-bg">
      <div className="relative flex max-w-[1500px] mx-auto">
        {sidebar}
        {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}

        <div className="flex-1 min-w-0 relative z-10">
          <header className="sticky top-0 z-30 px-4 sm:px-8 py-3 flex items-center gap-3
            backdrop-blur-xl border-b border-white/10 bg-[#0a1738]/70">
            <button onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-xl bg-white/10 text-white"
              data-testid="sidebar-toggle"><Menu size={18} /></button>
            <img src={ASSETS.logo} alt="" className="h-10 lg:hidden mix-blend-screen soft-mask-wide" />
            <div className="flex-1" />
            {right}
            <button data-testid="theme-toggle" onClick={() => { sfx.click(); toggle(); }}
              className="rounded-full p-2.5 text-white bg-white/10 hover:bg-white/20 border border-white/15"
              title={theme === "dark" ? "Day mode" : "Night mode"}>
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button data-testid="sound-toggle"
              onClick={() => { const next = !sound; setSound(next); sfx.toggle(next); if (next) sfx.click(); }}
              className="rounded-full p-2.5 text-white bg-white/10 hover:bg-white/20 border border-white/15"
              title="Sound">
              {sound ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button data-testid="lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="rounded-full px-3 py-2 text-sm font-bold text-white bg-white/10 hover:bg-white/20
                flex items-center gap-1.5 border border-white/15">
              <Globe size={15} />{lang === "ar" ? "EN" : "ع"}
            </button>
          </header>

          <main className="px-4 sm:px-8 py-8 pb-16">{children}</main>
        </div>
      </div>
      <PasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
};

export const Stat = ({ label, value, color = "var(--sky)", testid }) => (
  <div className="glass p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest font-bold text-white/60">{label}</div>
    <div className="text-3xl font-black mt-2" style={{ color }}>{value}</div>
  </div>
);

export const Modal = ({ open, onClose, title, children, testid }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-[#04102a]/70 backdrop-blur-md grid place-items-center p-4"
      onClick={onClose}>
      <div className="glass w-full max-w-lg p-6 max-h-[90vh] overflow-auto"
        data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-black text-white">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn"
            className="p-2 rounded-xl bg-white/10 text-white/80 hover:bg-white/20"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Panel = ({ children, className = "", testid }) => (
  <div className={`pro-card ${className}`} data-testid={testid}>{children}</div>
);
